// Supplierreport.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }
  return currency;
};

const SearchableDropdown = ({ options, value, onChange, placeholder, label }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (selected) => {
    onChange(selected);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }} ref={wrapperRef}>
      <label style={{ color: '#2e3192', fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ced4da', borderRadius: '0.375rem', background: 'white' }}>
        <input
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          style={{
            flex: 1,
            padding: '0.5rem',
            border: 'none',
            outline: 'none',
            borderRadius: '0.375rem',
            background: 'transparent',
            minWidth: '0',
          }}
        />
        <span style={{ padding: '0 8px', color: '#6c757d', cursor: 'pointer' }} onClick={() => setIsOpen(!isOpen)}>▼</span>
      </div>
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          maxHeight: '200px',
          overflowY: 'auto',
          background: 'white',
          border: '1px solid #ced4da',
          borderRadius: '0.375rem',
          zIndex: 1000,
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{ padding: '0.5rem', color: '#6c757d' }}>No options</div>
          ) : (
            filteredOptions.map(opt => (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                style={{
                  padding: '0.5rem',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f1f3f5',
                  background: opt.value === value ? '#f0f0ff' : 'white'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0ff'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const Supplierreport = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [batchNumbers, setBatchNumbers] = useState([]);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [selectedBatchNo, setSelectedBatchNo] = useState('');

  const printRef = useRef();

  const formatCurrency = useCallback((amount) => {
    const currencyCode = getCompanyCurrency(company);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [company]);

  const fetchCompany = useCallback(async () => {
    try {
      setLoadingCompany(true);
      const res = await axios.get(`${url}/api/company/current`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      setCompany(res.data);
    } catch (err) {
      setCompany(null);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoadingSuppliers(true);
      const res = await axios.get(`${url}/api/supplier/dropdown`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      if (res.data.success && Array.isArray(res.data.data)) setSuppliers(res.data.data);
      else setSuppliers([]);
    } catch (err) {
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;
      if (selectedSupplierId) params.supplier_id = selectedSupplierId;
      params.limit = 1000;

      const res = await axios.get(`${url}/api/purchase/invoices`, {
        headers: getAuthHeaders(),
        params,
      });
      const data = res.data.data || [];

      let formattedInvoices = await Promise.all(data.map(async (inv) => {
        let batchList = [];
        try {
          const txRes = await axios.get(`${url}/api/purchase/transactions/by-invoice/${inv._id}`, {
            headers: getAuthHeaders(),
          });
          const items = txRes.data.data || [];
          batchList = [...new Set(items.map(item => item.batch_number).filter(Boolean))];
        } catch (err) {
          // ignore
        }
        return {
          _id: inv._id,
          supplier_name: inv.supplier_id?.supplier_name || 'Unknown',
          invoice_no: inv.invoice_no,
          total_amount: inv.grand_total || 0,
          paid_amount: inv.total_paid_with_suppliers !== undefined
            ? inv.total_paid_with_suppliers
            : (inv.total_amount_paid || 0),
          balance: inv.actual_balance !== undefined
            ? inv.actual_balance
            : (inv.grand_total - (inv.total_paid_with_suppliers || inv.total_amount_paid || 0)),
          invoice_date: inv.invoice_date,
          batch_numbers: batchList
        };
      }));

      const allBatches = new Set();
      formattedInvoices.forEach(inv => {
        inv.batch_numbers.forEach(b => allBatches.add(b));
      });
      setBatchNumbers(Array.from(allBatches).sort());

      let filtered = formattedInvoices;
      if (selectedBatchNo) {
        filtered = filtered.filter(inv => inv.batch_numbers.includes(selectedBatchNo));
      }

      setInvoices(formattedInvoices);
      setFilteredData(filtered);
      if (filtered.length === 0 && (selectedBatchNo || formattedInvoices.length > 0)) {
        setError(selectedBatchNo ? `No invoices found with batch "${selectedBatchNo}".` : 'No invoices found for the selected filters.');
      } else {
        setError(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load supplier report');
      setInvoices([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, selectedSupplierId, selectedBatchNo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReport();
    }, 300);
    return () => clearTimeout(timer);
  }, [fromDate, toDate, selectedSupplierId, selectedBatchNo, fetchReport]);

  useEffect(() => {
    fetchCompany();
    fetchSuppliers();
  }, [fetchCompany, fetchSuppliers]);

  useEffect(() => {
    if (!loadingCompany && !loadingSuppliers) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCompany, loadingSuppliers]);

  const handleGenerate = () => fetchReport();

  // ✅ Single print handler — uses the browser's native print with the
  //    .print-only block below. No more new-window duplication.
  const handlePrint = () => window.print();

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

  const primaryColor = '#2e3192';
  const secondaryColor = '#f1592a';

  const getLogoUrl = () => {
    if (!company?.logo) return null;
    if (company.logo.startsWith('http')) return company.logo;
    const cleanPath = company.logo.replace(/^\/+/, '');
    return `${url}/${cleanPath}`;
  };

  const totalTotalAmount = filteredData.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const totalPaidAmount = filteredData.reduce((sum, inv) => sum + (inv.paid_amount || 0), 0);
  const totalBalance = filteredData.reduce((sum, inv) => sum + (inv.balance || 0), 0);

  const supplierOptions = suppliers.map(s => ({ value: s._id, label: s.supplier_name }));
  const batchOptions = batchNumbers.map(b => ({ value: b, label: b }));

  // ✅ Address + Phone 1 · 2 · 3
  const companyAddress = company?.admin_user?.Usercomp_address || company?.address || '';
  const companyContact = company?.admin_user?.Usercomp_contact || company?.contact || '';
  const companyContact2 = company?.admin_user?.Usercomp_contact_2 || company?.contact_2 || '';
  const companyContact3 = company?.admin_user?.Usercomp_contact_3 || company?.contact_3 || '';
  const companyEmail = company?.admin_user?.Usercomp_email || company?.admin_email || '';
  const phoneLine = [companyContact, companyContact2, companyContact3].filter(Boolean).join(' · ');

  const fiscalYearLabel = company?.fiscal_year_start
    ? new Date(company.fiscal_year_start).getFullYear()
    : new Date().getFullYear();

  const companyCurrency = getCompanyCurrency(company);

  const rangeLabel = (() => {
    if (fromDate && toDate) return `${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`;
    if (fromDate) return `From ${new Date(fromDate).toLocaleDateString()}`;
    if (toDate) return `Until ${new Date(toDate).toLocaleDateString()}`;
    return 'All Time';
  })();

  if (loadingCompany) {
    return <div style={{ color: secondaryColor, textAlign: 'center', marginTop: '2rem' }}>Loading company data...</div>;
  }

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', padding: '1.5rem' }}>
      {/* ==================================================
          SCREEN PREVIEW
      ================================================== */}
      <div className="screen-only" ref={printRef}>
        {/* HEADER */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '1.5rem', marginBottom: '1.5rem', borderLeft: `4px solid ${primaryColor}` }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {getLogoUrl() && (
                <img
                  src={getLogoUrl()}
                  alt="Logo"
                  style={{ height: '64px', maxWidth: '80px', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div>
                <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: primaryColor }}>{company?.company_name || 'Company Name'}</h1>
                <p style={{ color: '#6c757d', fontSize: '0.875rem' }}>{company?.business_type?.name || 'Business'} | Code: {company?.company_code || 'N/A'}</p>
              </div>
            </div>
            <div style={{ textAlign: 'right', color: '#495057', fontSize: '0.875rem' }}>
              <p>{companyAddress || 'No address'}</p>
              <p style={{ marginTop: '0.25rem' }}>
                📞 {companyContact || 'No phone'}
                {companyContact2 ? ` · ${companyContact2}` : ''}
                {companyContact3 ? ` · ${companyContact3}` : ''}
                <br />
                ✉️ {companyEmail || 'No email'}
              </p>
              <p>Fiscal year start: {company?.fiscal_year_start ? new Date(company.fiscal_year_start).toLocaleDateString() : 'Not set'}</p>
            </div>
          </div>
          <div style={{ marginTop: '1rem', borderTop: '1px solid #e9ecef', paddingTop: '0.5rem' }}>
            <h2 style={{ textAlign: 'center', color: secondaryColor }}>Supplier Report</h2>
          </div>
        </div>

        {/* FILTERS (no-print) */}
        <div className="no-print" style={{ backgroundColor: 'white', borderRadius: '0.5rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', alignItems: 'end' }}>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>From Date</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }} />
            </div>
            <div>
              <label style={{ color: primaryColor, fontSize: '0.875rem', marginBottom: '0.25rem', display: 'block' }}>To Date</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #ced4da' }} />
            </div>
            <div>
              <SearchableDropdown
                options={supplierOptions}
                value={selectedSupplierId}
                onChange={setSelectedSupplierId}
                placeholder="Search supplier..."
                label="Supplier"
              />
              {loadingSuppliers && <small style={{ display: 'block', marginTop: '4px' }}>Loading suppliers...</small>}
            </div>
            <div>
              <SearchableDropdown
                options={batchOptions}
                value={selectedBatchNo}
                onChange={setSelectedBatchNo}
                placeholder="Search batch..."
                label="Batch No"
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleGenerate} disabled={loading} style={{ flex: 1, backgroundColor: primaryColor, color: 'white', padding: '0.5rem', borderRadius: '0.375rem', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>{loading ? 'Loading...' : 'Generate Report'}</button>
              <button onClick={handlePrint} disabled={filteredData.length === 0} style={{ backgroundColor: secondaryColor, color: 'white', padding: '0.5rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer', opacity: filteredData.length === 0 ? 0.6 : 1 }}>🖨️ Print</button>
            </div>
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.75rem', fontStyle: 'italic' }}>
            * Date range filters invoice dates. Shows each purchase invoice with its payment status. Batch No filters invoices containing that batch.
          </div>
        </div>

        {/* TABLE */}
        <div style={{ backgroundColor: 'white', borderRadius: '0.5rem', overflow: 'hidden' }}>
          {error && <div style={{ padding: '1rem', color: 'red', textAlign: 'center' }}>{error}</div>}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: primaryColor, color: 'white' }}>
                  <tr>
                    <th style={{ padding: '0.75rem' }}>No.</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Supplier Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Invoice No</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Batch No(s)</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total Amount</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Paid Amount</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 && !loading && (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>No invoices found. Click Generate Report.</td>
                    </tr>
                  )}
                  {filteredData.map((inv, idx) => (
                    <tr key={inv._id} style={{ borderBottom: '1px solid #f1f3f5' }}>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>{idx + 1}</td>
                      <td style={{ padding: '0.75rem' }}>{inv.supplier_name}</td>
                      <td style={{ padding: '0.75rem' }}>{inv.invoice_no}</td>
                      <td style={{ padding: '0.75rem' }}>
                        {inv.batch_numbers && inv.batch_numbers.length > 0
                          ? inv.batch_numbers.join(', ')
                          : '—'}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(inv.total_amount)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(inv.paid_amount)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(inv.balance)}</td>
                    </tr>
                  ))}
                  {filteredData.length > 0 && (
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan="3" style={{ padding: '0.75rem', textAlign: 'right' }}>Grand Totals:</td>
                      <td></td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(totalTotalAmount)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(totalPaidAmount)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(totalBalance)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="hide-on-print" style={{ fontSize: '0.75rem', textAlign: 'center', marginTop: '1rem', color: '#adb5bd' }}>
          Generated on {new Date().toLocaleString()}
        </div>
      </div>

      {/* ==================================================
          PRINT-ONLY A4 REPORT
      ================================================== */}
      <div className="print-only print-page">
        {/* Company header */}
        <div className="print-header">
          <div className="print-header-left">
            {getLogoUrl() && (
              <img
                src={getLogoUrl()}
                alt="Logo"
                className="print-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="print-header-text">
              <h1 className="print-company-name">{company?.company_name || 'Company Name'}</h1>
              <p className="print-company-line">{companyAddress || 'No address'}</p>
              {phoneLine && <p className="print-company-line">Tel: {phoneLine}</p>}
              <p className="print-company-line">
                {companyEmail ? `Email: ${companyEmail}` : ''}
                {company?.company_code ? ` · Code: ${company.company_code}` : ''}
              </p>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-meta-item">
              <span className="print-meta-label">Report Date</span>
              <span className="print-meta-value">{new Date().toLocaleDateString()}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Fiscal Year</span>
              <span className="print-meta-value">{fiscalYearLabel}-{fiscalYearLabel + 1}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Currency</span>
              <span className="print-meta-value">{companyCurrency}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="print-title-block">
          <h2 className="print-report-title">SUPPLIER REPORT</h2>
          <p className="print-report-subtitle">
            {rangeLabel} · {filteredData.length} invoice(s)
          </p>
        </div>

        {/* Print table */}
        <table className="print-table">
          <thead>
            <tr>
              <th className="print-col-no">No.</th>
              <th>Supplier Name</th>
              <th>Invoice No</th>
              <th>Batch No(s)</th>
              <th className="print-col-money">Total Amount</th>
              <th className="print-col-money">Paid Amount</th>
              <th className="print-col-money">Balance</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="7" className="print-empty">
                  No invoices found for the selected filters.
                </td>
              </tr>
            ) : (
              filteredData.map((inv, idx) => (
                <tr key={inv._id || `p-${idx}`}>
                  <td className="print-col-no">{idx + 1}</td>
                  <td>{inv.supplier_name}</td>
                  <td>{inv.invoice_no}</td>
                  <td>
                    {inv.batch_numbers && inv.batch_numbers.length > 0
                      ? inv.batch_numbers.join(', ')
                      : '—'}
                  </td>
                  <td className="print-col-money">{formatCurrency(inv.total_amount)}</td>
                  <td className="print-col-money">{formatCurrency(inv.paid_amount)}</td>
                  <td className="print-col-money print-total-value">{formatCurrency(inv.balance)}</td>
                </tr>
              ))
            )}
            {filteredData.length > 0 && (
              <tr className="print-total-row">
                <td colSpan="4" className="print-total-label">GRAND TOTAL</td>
                <td className="print-col-money print-total-value">{formatCurrency(totalTotalAmount)}</td>
                <td className="print-col-money print-total-value">{formatCurrency(totalPaidAmount)}</td>
                <td className="print-col-money print-total-value">{formatCurrency(totalBalance)}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Signature footer */}
        <div className="print-footer">
          <div className="print-footer-row">
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Prepared By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Checked By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Approved By</span>
            </div>
          </div>
          <p className="print-footer-note">
            Generated on {new Date().toLocaleString()} · HantiZone IMS
          </p>
        </div>
      </div>

      {/* ==================================================
          INLINE PRINT STYLES
      ================================================== */}
      <style>{`
        .print-only { display: none; }

        @page {
          size: A4 portrait;
          margin: 12mm 14mm 14mm 14mm;
        }

        @media print {
          html, body {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 10pt;
          }

          /* Hide EVERYTHING (sidebar, menu, search, filters) */
          body * { visibility: hidden !important; }

          /* Show only the print block */
          .print-only,
          .print-only * { visibility: visible !important; }

          .print-only {
            position: absolute !important;
            left: 0; top: 0;
            width: 100%;
            display: block !important;
            padding: 0; margin: 0;
            background: #fff !important;
          }

          .screen-only,
          .no-print,
          .hide-on-print { display: none !important; }

          /* ---------- PRINT BLOCK LAYOUT ---------- */
          .print-page {
            font-family: "Helvetica Neue", Arial, sans-serif;
            color: #111;
          }

          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 10mm;
            padding-bottom: 4mm;
            border-bottom: 1.5pt solid #222;
            margin-bottom: 5mm;
          }
          .print-header-left { display: flex; align-items: center; gap: 6mm; flex: 1; }
          .print-logo { max-height: 18mm; max-width: 26mm; object-fit: contain; }
          .print-company-name { font-size: 14pt; font-weight: 700; color: #170f48; margin: 0 0 1mm 0; }
          .print-company-line { font-size: 8.5pt; color: #444; margin: 0.4mm 0; }
          .print-header-right { text-align: right; font-size: 8.5pt; }
          .print-meta-item { display: flex; justify-content: flex-end; gap: 4mm; margin-bottom: 1mm; }
          .print-meta-label { color: #666; }
          .print-meta-value { font-weight: 700; min-width: 26mm; text-align: right; color: #111; }

          .print-title-block { text-align: center; margin-bottom: 5mm; }
          .print-report-title { font-size: 14pt; font-weight: 700; letter-spacing: 2pt; color: #170f48; margin: 0 0 1mm 0; }
          .print-report-subtitle { font-size: 9.5pt; color: #555; margin: 0; }

          /* ---------- PRINT TABLE ---------- */
          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9pt;
            page-break-inside: auto;
          }
          .print-table thead { display: table-header-group; }
          .print-table tr { page-break-inside: avoid; }
          .print-table th,
          .print-table td {
            border: 0.5pt solid #999;
            padding: 3.5pt 5pt;
            vertical-align: middle;
          }
          .print-table thead th {
            background: #eeeeee !important;
            color: #000 !important;
            font-weight: 700;
            text-align: left;
            font-size: 8.5pt;
            text-transform: uppercase;
            letter-spacing: 0.3pt;
          }
          .print-col-no    { width: 10mm; text-align: center; }
          .print-col-money {
            width: 28mm;
            text-align: right;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
          }

          .print-total-row { background: #f4f4f4 !important; font-weight: 700; }
          .print-total-label { text-align: right; letter-spacing: 0.5pt; }
          .print-total-value { font-weight: 700; }
          .print-empty { text-align: center; padding: 12pt; font-style: italic; }

          /* ---------- PRINT FOOTER ---------- */
          .print-footer { margin-top: 10mm; page-break-inside: avoid; }
          .print-footer-row {
            display: flex;
            justify-content: space-between;
            gap: 8mm;
            margin-bottom: 5mm;
          }
          .print-signature { flex: 1; text-align: center; font-size: 8.5pt; color: #333; }
          .print-signature-line { border-top: 0.5pt solid #333; margin-bottom: 1.5mm; height: 10mm; }
          .print-footer-note { text-align: center; font-size: 7.5pt; color: #888; margin: 0; }
        }
      `}</style>
    </div>
  );
};

export default Supplierreport;
